/* eslint-disable no-console */
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import { FormType, Locales, FormErrors } from "@simplyk/common";
import { v4 } from "uuid";

import { AppFrontendProvider } from "../../../components/AppFrontendProvider/AppFrontendProvider";
import { ZeffyHead } from "../../../components/Head/ZeffyHead";
import NotFoundWithProviders from "../../../components/NotFound/NotFoundWithProviders";
import Thermometer from "../../../components/Thermometer/Thermometer";
import { ServerSideContext } from "../../../types/app";

import { getSSRDonationForm, getTranslation, provideOrganization } from "@/helpers/getStaticProps";
import { useTheme } from "@/hooks/useTheme";
import { DonationFormFrontendPageOutput } from "@/types/trpc";

type EmbedThermometerPageProps =
  | {
      donationForm: DonationFormFrontendPageOutput;
      isDeleted?: false;
    }
  | {
      donationForm?: null;
      isDeleted: true;
    };

const EmbedThermometerPage = ({ donationForm, isDeleted }: EmbedThermometerPageProps) => {
  const theme = useTheme(donationForm?.color, true);

  if (isDeleted) {
    return (
      <>
        <ZeffyHead />
        <NotFoundWithProviders deletedForm />
      </>
    );
  }

  return (
    <AppFrontendProvider
      theme={theme}
      isEmbed={true}
      organization={donationForm.organization}
      formType={FormType.DonationForm}
      formData={donationForm}
      appSection="forms"
    >
      <Container>
        <Grid container>
          <Thermometer
            max={donationForm.target}
            form={{ id: donationForm.id, formType: FormType.DonationForm }}
            gutterBottom
          />
        </Grid>
      </Container>
    </AppFrontendProvider>
  );
};

export const getServerSideProps = async (context: ServerSideContext<{ donationFormId: string }>) => {
  const requestId = (context.req?.headers?.["x-request-id"] as string) || v4();
  const { donationFormId } = context.params;
  const locale = context.locale.toUpperCase() as Locales;

  try {
    const [donationFormResponse, translations] = await Promise.all([
      getSSRDonationForm({ donationFormIdOrUrlPath: donationFormId, requestId }),
      getTranslation(context.locale),
    ]);

    if (donationFormResponse.error) {
      const code = donationFormResponse.error.code;

      if (code === FormErrors.FormDeleted) {
        if (context.res) {
          context.res.statusCode = 410;
        }

        console.log(`Form deleted - redirecting to gone donationFormId=${donationFormId} ssrId=${requestId}`);
        return {
          props: {
            ...translations,
            isDeleted: true,
          },
        };
      }

      console.log(
        `NEXT_ERROR: form not found - redirecting to notFound donationFormId=${donationFormId} ssrId=${requestId}`
      );
      return { props: {}, notFound: true };
    }

    return {
      props: {
        ...translations,
        donationForm: donationFormResponse.data.organization
          ? donationFormResponse.data
          : provideOrganization(donationFormResponse.data, locale),
      },
    };
  } catch (error) {
    console.log(`NEXT_ERROR: request failed - redirecting to notFound ssrId=${requestId}`, error);
    return { props: {}, notFound: true };
  }
};

export default EmbedThermometerPage;
